/**
 * State-machine helpers for the Natural-Language Debug Feedback Widget.
 *
 * Kept separate from the React component so the transition logic can be
 * unit-tested in isolation. See nl_debug_widget_spec.md §5.
 *
 * States: prompt → grading → feedback → resolved
 */

import { DebugFeedbackResult, DebugNextAction, DebugVerdict } from "../api/client";

export type DebugState = "prompt" | "grading" | "feedback" | "resolved";

export const MIN_RESPONSE_CHARS = 20;

/** A submission is allowed only when the response meets the length floor. */
export function canSubmit(response: string): boolean {
  return response.trim().length >= MIN_RESPONSE_CHARS;
}

/**
 * The state to enter after a grade returns.
 *
 * Every graded verdict lands in `feedback`; the widget only moves to
 * `resolved` when the student self-attests or reveals the answer (handled
 * by explicit actions, not the grade itself).
 */
export function stateAfterGrade(_result: DebugFeedbackResult): DebugState {
  return "feedback";
}

/** Human-readable, color-independent label for a verdict chip (a11y). */
export function verdictLabel(verdict: DebugVerdict): string {
  switch (verdict) {
    case "on_track":
      return "On track";
    case "partial":
      return "Partially there";
    case "off_track":
      return "Off track";
    case "misconception_match":
      return "Common misconception";
    case "cannot_evaluate":
      return "Needs more detail";
  }
}

/** Whether the feedback view should surface a remediation link button. */
export function showsRemediation(result: DebugFeedbackResult): boolean {
  return (
    result.remediation_link !== null &&
    (result.next_action === "see_remediation" ||
      result.next_action === "skip_or_reveal" ||
      result.verdict === "misconception_match")
  );
}

/** Whether the widget should offer the skip-or-reveal door (attempt cap hit). */
export function showsSkipOrReveal(result: DebugFeedbackResult): boolean {
  return result.next_action === "skip_or_reveal";
}

/** Whether the student may keep iterating from the current feedback. */
export function canRetry(result: DebugFeedbackResult): boolean {
  if (result.next_action === "skip_or_reveal") {
    return false;
  }
  // cannot_evaluate doesn't burn an attempt, so iterating is always allowed.
  if (result.verdict === "cannot_evaluate") {
    return true;
  }
  return result.attempts_remaining > 0;
}

/** Map a next_action to the primary call-to-action label shown on the button. */
export function primaryActionLabel(action: DebugNextAction): string {
  switch (action) {
    case "iterate":
      return "Refine my answer";
    case "elaborate":
      return "Add more detail";
    case "see_hint":
      return "Try again";
    case "see_remediation":
      return "Try again";
    case "skip_or_reveal":
      return "Skip for now";
  }
}
